import {
    ActionButton,
    Dropdown,
    IDropdownOption,
    SearchBox
} from "@fluentui/react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_DEBOUNCE_MS = 400;
const DEFAULT_MINIMUM_BACKEND_SEARCH_LENGTH = 5;

interface ISearchableMultiSelectComboBoxProps {
    label: string;
    options: IDropdownOption[];
    selectedKeys: string[];
    onSelectionChange: (selectedKeys: string[]) => void;
    multiSelect?: boolean;
    onSearch?: (searchText: string) => void;
    searchPlaceholder?: string;
    placeholder?: string;
    noResultsText?: string;
    required?: boolean;
    disabled?: boolean;
    debounceMs?: number;
    minimumBackendSearchLength?: number;
}

const normalize = (value: string): string => value.trim().toLowerCase();

const SearchableMultiSelectComboBox: React.FunctionComponent<ISearchableMultiSelectComboBoxProps> = ({
    label,
    options,
    selectedKeys,
    onSelectionChange,
    multiSelect = true,
    onSearch,
    placeholder,
    searchPlaceholder = "Search",
    noResultsText = "No results found",
    required = false,
    disabled = false,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    minimumBackendSearchLength = DEFAULT_MINIMUM_BACKEND_SEARCH_LENGTH
}) => {
    const [searchText, setSearchText] = useState("");
    const lastRequestedSearchRef = useRef("");
    const normalizedSearchText = normalize(searchText);

    const visibleOptions = useMemo(
        () => {
            const matchingOptions = normalizedSearchText.length === 0
                ? options
                : options.filter(option => {
                    const key = normalize(String(option.key));
                    const text = normalize(option.text ?? "");

                    return (
                        key.includes(normalizedSearchText) ||
                        text.includes(normalizedSearchText)
                    );
                });

            return matchingOptions;
        },
        [ options, normalizedSearchText ]
    );

    const selectableVisibleKeys =
        useMemo(
            () =>
                visibleOptions
                    .filter(option => !option.disabled)
                    .map(option => String(option.key)),
            [visibleOptions]
        );

    const selectedKeySet =
        useMemo(
            () => new Set(selectedKeys),
            [selectedKeys]
        );

    const canSelectAllShown = multiSelect && selectableVisibleKeys.some(key => !selectedKeySet.has(key));

    useEffect(
        () => {
            if (!onSearch || normalizedSearchText.length < minimumBackendSearchLength) {
                if (normalizedSearchText.length === 0) {
                    lastRequestedSearchRef.current = "";
                }
                return;
            }

            if (lastRequestedSearchRef.current === normalizedSearchText) {
                return;
            }

            const timer = window.setTimeout(
                () => {
                    lastRequestedSearchRef.current = normalizedSearchText;
                    onSearch(searchText.trim());
                },
                debounceMs
            );

            return () => { window.clearTimeout(timer); };
        },
        [ searchText, normalizedSearchText, onSearch, debounceMs, minimumBackendSearchLength ]
    );

    const commitSelection = useCallback(
        (nextSelectedKeys: string[]) => {
            const hasChanged =
                nextSelectedKeys.length !== selectedKeys.length ||
                nextSelectedKeys.some((key, index) => key !== selectedKeys[index]);

            if (hasChanged) {
                onSelectionChange(nextSelectedKeys);
            }
        },
        [selectedKeys, onSelectionChange]
    );

    const handleSelectionChange = useCallback(
        ( _event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
            if (!option || option.disabled) {
                return;
            }

            const selectedKey = String(option.key);

            if (!multiSelect) {
                if (selectedKeys[0] === selectedKey) {
                    commitSelection([]);
                } else {
                    commitSelection([selectedKey]);
                }
                return;
            }

            const nextSelection = new Set(selectedKeys);

            if (nextSelection.has(selectedKey)) {
                nextSelection.delete(selectedKey);
            } else {
                nextSelection.add(selectedKey);
            }

            const nextSelectedKeys = [
                ...selectedKeys.filter(key => nextSelection.has(key)),
                ...Array.from(nextSelection).filter(
                    key => !selectedKeySet.has(key)
                )
            ];

            commitSelection(nextSelectedKeys);
        },
        [ selectedKeys, selectedKeySet, commitSelection, multiSelect ]
    );

    const selectAllShown =
        useCallback(() => {
            const nextSelection = new Set(selectedKeys);

            selectableVisibleKeys.forEach(key =>
                nextSelection.add(key)
            );

            commitSelection([
                ...selectedKeys,
                ...selectableVisibleKeys.filter(
                    key => !selectedKeySet.has(key)
                )
            ]);
        }, [ selectedKeys, selectedKeySet, selectableVisibleKeys, commitSelection ]);

    const clearAll =
        useCallback(() => { commitSelection([]); }, [commitSelection]);

    const dropdownOptions = useMemo<IDropdownOption[]>(() => {
        const hasSearchText = normalizedSearchText.length > 0;

        const optionsWithVisibility = options.map(option => {
            const key = normalize(String(option.key));
            const text = normalize(option.text ?? "");

            const matchesSearch =
                !hasSearchText ||
                key.includes(normalizedSearchText) ||
                text.includes(normalizedSearchText);

            return { ...option, hidden: !matchesSearch };
        });

        if (visibleOptions.length === 0) {
            optionsWithVisibility.push({
                key: "__no_results__",
                text: noResultsText,
                disabled: true,
                hidden: false
            });
        }

        return optionsWithVisibility;
    }, [ options, normalizedSearchText, visibleOptions.length, noResultsText ]);

    const selectedDisplayText = useMemo(() => {
        if (selectedKeys.length === 0) {
            return "";
        }

        return selectedKeys
            .map((key) => {
                const matchedOption = options.find(option => String(option.key) === key);
                return matchedOption?.text || key;
            })
            .join(", ");
    }, [selectedKeys, options]);

    return (
        <div className="searchableMultiSelectComboBox">
            <Dropdown
                label={`${label}:`}
                options={dropdownOptions}
                selectedKeys={selectedKeys}
                onChange={handleSelectionChange}
                onDismiss={() => {
                    setSearchText("");
                }}
                onRenderTitle={() => {
                    if (selectedKeys.length === 0) {
                        return null;
                    }

                    return (
                        <span title={selectedDisplayText}>
                            {selectedDisplayText}
                        </span>
                    );
                }}
                onRenderList={(dropdownProps, defaultRender) => (
                    <div className="searchableMultiSelectList">
                        <div className="searchableMultiSelectMenuHeader">
                            <SearchBox
                                placeholder={searchPlaceholder}
                                value={searchText}
                                showIcon
                                disableAnimation
                                onChange={(_event, value) =>
                                    setSearchText(value || "")
                                }
                            />

                            <div className="searchableMultiSelectActions">
                                {multiSelect && (
                                    <ActionButton
                                        type="button"
                                        disabled={disabled || !canSelectAllShown}
                                        onClick={selectAllShown}
                                    >
                                        Select All
                                    </ActionButton>
                                )}

                                <ActionButton
                                    type="button"
                                    disabled={disabled || selectedKeys.length === 0}
                                    onClick={clearAll}
                                >
                                    Clear All
                                </ActionButton>
                            </div>
                        </div>

                        {defaultRender?.(dropdownProps)}
                    </div>
                )}
                multiSelect={multiSelect}
                selectedKey={!multiSelect ? (selectedKeys[0] ?? undefined) : undefined}
                multiSelectDelimiter=", "
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                calloutProps={{
                    calloutMaxHeight: 270,
                    className: "searchableMultiSelectCallout"
                }}
            />
        </div>
    );
};

export default React.memo(SearchableMultiSelectComboBox);